import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_encounter-god-change/artifacts/rn5hnru9_CFM%20Logo.jpg";

const NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/events", label: "Events", testid: "nav-events" },
  { to: "/testimonies", label: "Testimonies", testid: "nav-testimonies" },
  { to: "/give", label: "Give", testid: "nav-give" },
];

// Pages that start with a dark hero — header should use light text until scrolled
const DARK_HERO_ROUTES = [];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onDarkHero = DARK_HERO_ROUTES.includes(pathname) && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const logoText = onDarkHero ? "text-white" : "text-[#0B1D2E]";
  const logoSub = onDarkHero ? "text-white/60" : "text-[#4A5562]";
  const menuBtn = onDarkHero ? "text-white bg-white/10 border-white/20" : "text-[#0B1D2E] bg-white/70 border-black/5";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="The Potter's House — Christian Fellowship Ministries logo"
            className="w-11 h-11 object-contain transition-transform group-hover:scale-105"
            style={{ mixBlendMode: onDarkHero ? "screen" : "multiply" }}
          />
          <div className="leading-tight">
            <div className={`font-serif text-lg font-semibold ${logoText}`}>The Potter's House</div>
            <div className={`text-[10px] tracking-[0.22em] uppercase ${logoSub}`}>Christian Fellowship Ministries</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={link.testid}
              className={({ isActive }) =>
                `px-4 py-2 text-sm tracking-wide transition-colors rounded-full ${
                  onDarkHero
                    ? isActive
                      ? "text-white font-medium"
                      : "text-white/70 hover:text-white"
                    : isActive
                    ? "text-[#0B1D2E] font-medium"
                    : "text-[#4A5562] hover:text-[#0B1D2E]"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <button
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className={`md:hidden w-10 h-10 rounded-full flex items-center justify-center backdrop-blur border transition-colors ${menuBtn}`}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-black/5 px-6 py-6" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`mobile-${link.testid}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-base rounded-lg transition-colors ${
                    isActive
                      ? "bg-[#F3F2EB] text-[#0B1D2E] font-medium"
                      : "text-[#4A5562] hover:bg-[#F3F2EB]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
